import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, CircleMarker, Polyline, useMapEvents, useMap, Popup, ZoomControl } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { Box, Paper, Link } from '@mui/material';
import { calculateLeaseCentroids, createLeaseWellConnections } from '../../utils/leaseDataProcessor';
import { getFlagIconHTML } from '../shared/FlagIcon';
import { generateLeaseKey } from '../../utils/leaseKeyUtils';
import {
  leaseHasSelectedFlags,
  countyHasSelectedFlags,
  getWellLeaseIdentity,
  getLeaseSelectedFlagCount,
  getCountySelectedFlagMetrics,
} from '../../utils/flagFilterUtils';
import {
  getCountyColor as getCountyColorFromScale,
  getCountyOpacity as getCountyOpacityFromScale,
  getCountyFlagColor as getCountyFlagColorFromScale,
  getCountyFlagOpacity as getCountyFlagOpacityFromScale,
  getLeaseFlagColor,
  getLeaseBlueColor,
} from '../../utils/countyColorScale';
import CountyTiles from './CountyTiles';
import BoxSelector from './BoxSelector';
import FreehandSelector from './FreehandSelector';
import logger from '../../utils/logger';

// Component to track zoom changes and map bounds
const ZoomTracker = ({ onZoomChange, onBoundsChange }) => {
  const map = useMapEvents({
    zoomend: () => {
      onZoomChange(map.getZoom());
      if (onBoundsChange) {
        onBoundsChange(map.getBounds());
      }
    },
    zoom: () => {
      onZoomChange(map.getZoom());
    },
    moveend: () => {
      if (onBoundsChange) {
        onBoundsChange(map.getBounds());
      }
    }
  });
  return null;
};

// Component to expose map instance to parent
const MapInstanceExposer = ({ onMapReady }) => {
  const map = useMap();
  
  useEffect(() => {
    if (onMapReady) {
      onMapReady(map);
    }
  }, [map, onMapReady]);

  return null;
};

// Component to adjust map bounds when view mode changes
const MapBoundsAdjuster = ({ viewMode, leaseCentroids, wellsData }) => {
  const map = useMap();
  const lastViewModeRef = useRef(null);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Initialize map view only once on first mount
    if (!hasInitializedRef.current) {
      map.setView([31.0, -99.0], 6);
      hasInitializedRef.current = true;
      lastViewModeRef.current = viewMode;
      return;
    }

    // Only adjust bounds when view mode actually changes, not on every render
    if (lastViewModeRef.current === viewMode) {
      return;
    }
    
    lastViewModeRef.current = viewMode;

    // Don't auto-zoom when switching view modes - only preserve current view
    // Auto-zoom should only happen when filters are applied/cleared
    // When switching to county view, preserve current zoom and center (don't reset)
    // When switching to lease view, also preserve current zoom and center
  }, [viewMode, leaseCentroids, map, wellsData]);

  return null;
};

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Icon cache for well markers
const wellIconCache = new Map();
const leaseIconCache = new Map();

// Helper to get cache key for well icon
const getWellIconCacheKey = (wellGroup, color, radius, multiGroupCount, isSelected) => {
  return `${wellGroup}-${color}-${radius}-${multiGroupCount}-${isSelected}`;
};

// Helper to get cache key for lease icon
const getLeaseIconCacheKey = (color, size, wellCount, opacity, isSelected, flagCount) => {
  return `${color}-${size}-${wellCount}-${opacity}-${isSelected}-${flagCount || 0}`;
};

// Create custom well marker icon based on Well Group (with caching)
const createWellMarkerIcon = (wellGroup, color, radius, multiGroupCount = 1, isSelected = false) => {
  const cacheKey = getWellIconCacheKey(wellGroup, color, radius, multiGroupCount, isSelected);
  if (wellIconCache.has(cacheKey)) {
    return wellIconCache.get(cacheKey);
  }
  
  // Original icon creation logic
  const size = radius * 2;
  const borderColor = '#ffffff';
  const borderWidth = 2;
  const shadow = '0 2px 4px rgba(0,0,0,0.3)';
  
  // Selection highlight: bright blue border and glow
  const selectionBorderColor = '#2196f3';
  const selectionBorderWidth = isSelected ? 4 : borderWidth;
  const selectionGlow = isSelected ? `0 0 0 ${borderWidth + 2}px rgba(33, 150, 243, 0.6), 0 0 0 ${borderWidth + 4}px rgba(33, 150, 243, 0.3), ${shadow}` : shadow;
  const actualBorderColor = isSelected ? selectionBorderColor : borderColor;
  
  let html = '';
  
  // Normalize well group for comparison
  const normalizedGroup = (wellGroup || 'UNKNOWN').toUpperCase().trim();
  
  switch (normalizedGroup) {
    case 'ACTIVE':
      // Circle with fill
      const activeFillColor = color === 'transparent' ? 'transparent' : color;
      html = `<div style="
        width: ${size}px;
        height: ${size}px;
        background-color: ${activeFillColor};
        border: ${selectionBorderWidth}px solid ${actualBorderColor};
        border-radius: 50%;
        box-shadow: ${selectionGlow};
      "></div>`;
      break;
      
    case 'PERMIT':
      // Pentagon (5-angle shape) with fill
      const pentagonPoints = [];
      const center = size / 2;
      const pentRadius = radius - borderWidth;
      for (let i = 0; i < 5; i++) {
        const angle = (i * 2 * Math.PI / 5) - (Math.PI / 2);
        const x = center + pentRadius * Math.cos(angle);
        const y = center + pentRadius * Math.sin(angle);
        pentagonPoints.push(`${x},${y}`);
      }
      const pentagonFill = color === 'transparent' ? 'none' : color;
      html = `<svg width="${size}" height="${size}" style="filter: drop-shadow(${selectionGlow});">
        ${isSelected ? `<circle cx="${center}" cy="${center}" r="${radius + 2}" fill="none" stroke="${selectionBorderColor}" stroke-width="2" opacity="0.6"/>` : ''}
        <polygon 
          points="${pentagonPoints.join(' ')}" 
          fill="${pentagonFill}" 
          stroke="${actualBorderColor}" 
          stroke-width="${selectionBorderWidth}"
        />
      </svg>`;
      break;
      
    case 'SHUT IN':
      // Circle with half fill
      const shutInRadius = radius - borderWidth;
      const shutInCenter = size / 2;
      const shutInFill = color === 'transparent' ? 'none' : color;
      html = `<svg width="${size}" height="${size}" style="filter: drop-shadow(${selectionGlow});">
        ${isSelected ? `<circle cx="${shutInCenter}" cy="${shutInCenter}" r="${shutInRadius + 2}" fill="none" stroke="${selectionBorderColor}" stroke-width="2" opacity="0.6"/>` : ''}
        <path 
          d="M ${shutInCenter} ${shutInCenter} L ${shutInCenter} ${shutInCenter - shutInRadius} A ${shutInRadius} ${shutInRadius} 0 0 1 ${shutInCenter} ${shutInCenter + shutInRadius} Z" 
          fill="${shutInFill}" 
        />
        <circle 
          cx="${shutInCenter}" 
          cy="${shutInCenter}" 
          r="${shutInRadius}" 
          fill="none" 
          stroke="${actualBorderColor}" 
          stroke-width="${selectionBorderWidth}"
        />
      </svg>`;
      break;
      
    case 'TA':
      // Triangle with half fill
      const triangleTopX = size / 2;
      const triangleTopY = borderWidth;
      const triangleBottomRightX = size - borderWidth;
      const triangleBottomRightY = size - borderWidth;
      const triangleBottomLeftX = borderWidth;
      const triangleBottomLeftY = size - borderWidth;
      const triangleFill = color === 'transparent' ? 'none' : color;
      // Half fill: right half of triangle (from center vertical line to right side)
      html = `<svg width="${size}" height="${size}" style="filter: drop-shadow(${selectionGlow});">
        ${isSelected ? `<polygon points="${triangleTopX},${triangleTopY} ${triangleBottomRightX},${triangleBottomRightY} ${triangleBottomLeftX},${triangleBottomLeftY}" fill="none" stroke="${selectionBorderColor}" stroke-width="4" opacity="0.6"/>` : ''}
        <path 
          d="M ${triangleTopX} ${triangleTopY} L ${triangleTopX} ${triangleBottomRightY} L ${triangleBottomRightX} ${triangleBottomRightY} Z" 
          fill="${triangleFill}" 
        />
        <polygon 
          points="${triangleTopX},${triangleTopY} ${triangleBottomRightX},${triangleBottomRightY} ${triangleBottomLeftX},${triangleBottomLeftY}" 
          fill="none" 
          stroke="${actualBorderColor}" 
          stroke-width="${selectionBorderWidth}"
        />
      </svg>`;
      break;
      
    case 'PARTIAL PLUG':
      // Same as TA - Triangle with half fill
      const partialTriangleTopX = size / 2;
      const partialTriangleTopY = borderWidth;
      const partialTriangleBottomRightX = size - borderWidth;
      const partialTriangleBottomRightY = size - borderWidth;
      const partialTriangleBottomLeftX = borderWidth;
      const partialTriangleBottomLeftY = size - borderWidth;
      const partialTriangleFill = color === 'transparent' ? 'none' : color;
      // Half fill: right half of triangle (from center vertical line to right side)
      html = `<svg width="${size}" height="${size}" style="filter: drop-shadow(${selectionGlow});">
        ${isSelected ? `<polygon points="${partialTriangleTopX},${partialTriangleTopY} ${partialTriangleBottomRightX},${partialTriangleBottomRightY} ${partialTriangleBottomLeftX},${partialTriangleBottomLeftY}" fill="none" stroke="${selectionBorderColor}" stroke-width="4" opacity="0.6"/>` : ''}
        <path 
          d="M ${partialTriangleTopX} ${partialTriangleTopY} L ${partialTriangleTopX} ${partialTriangleBottomRightY} L ${partialTriangleBottomRightX} ${partialTriangleBottomRightY} Z" 
          fill="${partialTriangleFill}" 
        />
        <polygon 
          points="${partialTriangleTopX},${partialTriangleTopY} ${partialTriangleBottomRightX},${partialTriangleBottomRightY} ${partialTriangleBottomLeftX},${partialTriangleBottomLeftY}" 
          fill="none" 
          stroke="${actualBorderColor}" 
          stroke-width="${selectionBorderWidth}"
        />
      </svg>`;
      break;
      
    case 'P&A':
      // Circle with diagonal line through it, with fill
      const paFill = color === 'transparent' ? 'none' : color;
      const paCenter = size / 2;
      html = `<svg width="${size}" height="${size}" style="filter: drop-shadow(${selectionGlow});">
        ${isSelected ? `<circle cx="${paCenter}" cy="${paCenter}" r="${radius + 2}" fill="none" stroke="${selectionBorderColor}" stroke-width="2" opacity="0.6"/>` : ''}
        <circle 
          cx="${paCenter}" 
          cy="${paCenter}" 
          r="${radius - borderWidth}" 
          fill="${paFill}" 
          stroke="${actualBorderColor}" 
          stroke-width="${selectionBorderWidth}"
        />
        <line 
          x1="${borderWidth}" 
          y1="${borderWidth}" 
          x2="${size - borderWidth}" 
          y2="${size - borderWidth}" 
          stroke="${actualBorderColor}" 
          stroke-width="${selectionBorderWidth}"
        />
      </svg>`;
      break;
      
    case 'DRY HOLE':
      // Circle with cross over it, with fill
      const dryHoleFill = color === 'transparent' ? 'none' : color;
      const dryHoleCenter = size / 2;
      html = `<svg width="${size}" height="${size}" style="filter: drop-shadow(${selectionGlow});">
        ${isSelected ? `<circle cx="${dryHoleCenter}" cy="${dryHoleCenter}" r="${radius + 2}" fill="none" stroke="${selectionBorderColor}" stroke-width="2" opacity="0.6"/>` : ''}
        <circle 
          cx="${dryHoleCenter}" 
          cy="${dryHoleCenter}" 
          r="${radius - borderWidth}" 
          fill="${dryHoleFill}" 
          stroke="${actualBorderColor}" 
          stroke-width="${selectionBorderWidth}"
        />
        <line 
          x1="${borderWidth}" 
          y1="${borderWidth}" 
          x2="${size - borderWidth}" 
          y2="${size - borderWidth}" 
          stroke="${actualBorderColor}" 
          stroke-width="${selectionBorderWidth}"
        />
        <line 
          x1="${size - borderWidth}" 
          y1="${borderWidth}" 
          x2="${borderWidth}" 
          y2="${size - borderWidth}" 
          stroke="${actualBorderColor}" 
          stroke-width="${selectionBorderWidth}"
        />
      </svg>`;
      break;
      
    case 'MULTI':
      // Hexagon (6-sided) for multiple well groups
      // Make hexagon larger so a circle of the given radius fits inside it
      // For a regular hexagon, if we want a circle of radius R to fit inside (inscribed circle),
      // the hexagon's circumradius should be R / cos(30°) = R / (√3/2) ≈ 1.155 * R
      const hexagonPoints = [];
      const hexCenter = size / 2;
      // Increase hexagon radius so circle fits inside: divide by cos(30°) = √3/2
      const hexRadius = (radius - borderWidth) / (Math.sqrt(3) / 2);
      for (let i = 0; i < 6; i++) {
        const angle = (i * 2 * Math.PI / 6) - (Math.PI / 2);
        const x = hexCenter + hexRadius * Math.cos(angle);
        const y = hexCenter + hexRadius * Math.sin(angle);
        hexagonPoints.push(`${x},${y}`);
      }
      const multiFill = color === 'transparent' ? 'none' : color;
      html = `<svg width="${size}" height="${size}" style="filter: drop-shadow(${selectionGlow});">
        ${isSelected ? `<circle cx="${hexCenter}" cy="${hexCenter}" r="${radius + 2}" fill="none" stroke="${selectionBorderColor}" stroke-width="2" opacity="0.6"/>` : ''}
        <polygon 
          points="${hexagonPoints.join(' ')}" 
          fill="${multiFill}" 
          stroke="${actualBorderColor}" 
          stroke-width="${selectionBorderWidth}"
        />
        ${multiGroupCount > 1 ? `<text x="${hexCenter}" y="${hexCenter + 3}" text-anchor="middle" fill="${actualBorderColor}" font-size="${Math.max(8, radius - 4)}px" font-weight="bold">${multiGroupCount}</text>` : ''}
      </svg>`;
      break;
      
    default:
      // Default: circle with fill
      html = `<div style="
        width: ${size}px;
        height: ${size}px;
        background-color: ${color};
        border: ${selectionBorderWidth}px solid ${actualBorderColor};
        border-radius: 50%;
        box-shadow: ${selectionGlow};
      "></div>`;
      break;
  }
  
  const icon = L.divIcon({
    className: 'well-marker',
    html: html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
  
  // Cache the icon
  wellIconCache.set(cacheKey, icon);
  return icon;
};

// Create custom lease marker icon (square/diamond shape to differentiate from wells) (with caching)
const createLeaseMarkerIcon = (color, size, wellCount, opacity = 1, isSelected = false, flagCount = 0) => {
  const cacheKey = getLeaseIconCacheKey(color, size, wellCount, opacity, isSelected, flagCount);
  if (leaseIconCache.has(cacheKey)) {
    return leaseIconCache.get(cacheKey);
  }
  
  // Original icon creation logic
  // Convert hex color to rgba for opacity support
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  const bgColor = opacity < 1 ? hexToRgba(color, opacity) : color;
  
  // Selection highlight: bright blue border and glow
  const selectionBorderColor = '#2196f3';
  const borderWidth = isSelected ? 4 : 3;
  const borderColor = isSelected ? selectionBorderColor : 'white';
  const selectionGlow = isSelected ? `0 0 0 ${borderWidth + 2}px rgba(33, 150, 243, 0.6), 0 0 0 ${borderWidth + 4}px rgba(33, 150, 243, 0.3), 0 2px 6px rgba(0,0,0,0.4)` : '0 2px 6px rgba(0,0,0,0.4)';
  
  // Determine font size based on marker size
  const fontSize = size <= 14 ? '8px' : size <= 18 ? '10px' : '12px';
  const fontWeight = '600';
  
  // Red bubble indicator (positioned at top-right corner)
  // Small circular badge that doesn't mask the well count number
  const flagBadgeHTML = flagCount > 0 ? `<div style="
    position: absolute;
    top: -9px;
    right: 2px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 10px;
    height: 10px;
    background-color: #f44336;
    border-radius: 50%;
    border: 1.5px solid white;
    box-shadow: 0 1px 2px rgba(0,0,0,0.3);
    z-index: 10;
  "></div>` : '';
  
  const icon = L.divIcon({
    className: 'lease-marker',
    html: `<div style="
      width: ${size}px;
      height: ${size}px;
      background-color: ${bgColor};
      border: ${borderWidth}px solid ${borderColor};
      border-radius: 4px;
      transform: rotate(45deg);
      box-shadow: ${selectionGlow};
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <span style="
        transform: rotate(-45deg);
        color: white;
        font-size: ${fontSize};
        font-weight: ${fontWeight};
        text-shadow: 0 1px 2px rgba(0,0,0,0.5);
        line-height: 1;
      ">${wellCount}</span>
      ${flagBadgeHTML}
    </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
  
  // Cache the icon
  leaseIconCache.set(cacheKey, icon);
  return icon;
};

// Create small square icon for bottomhole markers (outline only, no fill)
const createBottomholeIcon = (color) => {
  const size = 10; // Small square size
  const html = `<div style="
    width: ${size}px;
    height: ${size}px;
    background-color: transparent;
    border: 1.5px solid ${color};
    box-shadow: 0 1px 2px rgba(0,0,0,0.3);
  "></div>`;
  
  return L.divIcon({
    className: 'bottomhole-marker',
    html: html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
};

// Get color based on inactive years (currently unused but kept for potential future use)
/*
const getInactiveYearsColor = (inactiveYears) => {
  const years = inactiveYears || 0;
  
  // 0 years: no fill (transparent/outline only)
  if (years === 0) {
    return 'transparent';
  }
  
  // 1-7 years: green to yellow gradient
  if (years >= 1 && years <= 7) {
    const ratio = (years - 1) / 6; // 0 to 1
    // Green (#4caf50) to Yellow (#ffc107)
    const r1 = 76, g1 = 175, b1 = 80;   // Green RGB
    const r2 = 255, g2 = 193, b2 = 7;   // Yellow RGB
    const r = Math.round(r1 + (r2 - r1) * ratio);
    const g = Math.round(g1 + (g2 - g1) * ratio);
    const b = Math.round(b1 + (b2 - b1) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  }
  
  // 7-14 years: yellow to red gradient
  if (years > 7 && years <= 14) {
    const ratio = (years - 7) / 7; // 0 to 1
    // Yellow (#ffc107) to Red (#f44336)
    const r1 = 255, g1 = 193, b1 = 7;   // Yellow RGB
    const r2 = 244, g2 = 67, b2 = 54;  // Red RGB
    const r = Math.round(r1 + (r2 - r1) * ratio);
    const g = Math.round(g1 + (g2 - g1) * ratio);
    const b = Math.round(b1 + (b2 - b1) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  }
  
  // 14-21 years: red to dark red gradient
  if (years > 14 && years <= 21) {
    const ratio = (years - 14) / 7; // 0 to 1
    // Red (#f44336) to Dark Red (#c62828)
    const r1 = 244, g1 = 67, b1 = 54;   // Red RGB
    const r2 = 198, g2 = 40, b2 = 40;   // Dark Red RGB
    const r = Math.round(r1 + (r2 - r1) * ratio);
    const g = Math.round(g1 + (g2 - g1) * ratio);
    const b = Math.round(b1 + (b2 - b1) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  }
  
  // 21+ years: dark red
  return '#c62828'; // Dark red
};
*/

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/** Map a metric into [rangeMin, rangeMax] using dataset min/max */
const scaleLinear = (value, domainMin, domainMax, rangeMin, rangeMax) => {
  if (!(domainMax > domainMin)) {
    return (rangeMin + rangeMax) / 2;
  }
  const t = clamp((Number(value) - domainMin) / (domainMax - domainMin), 0, 1);
  return rangeMin + t * (rangeMax - rangeMin);
};

const WELL_RADIUS_MIN = 6;
const WELL_RADIUS_MAX = 16;
const LEASE_RADIUS_MIN = 7;
const LEASE_RADIUS_MAX = 14;

const normalizeDomain = (min, max) => {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { min: 0, max: 1 };
  }
  if (min === max) {
    return { min, max: min + 1 };
  }
  return { min, max };
};

// Get well marker style based on attributes
// Size: inactive_years mapped into [WELL_RADIUS_MIN, WELL_RADIUS_MAX]
const getWellMarkerStyle = (well, sizeOpts = {}) => {
  const wellGroup = well['Well Group'] || well.well_group || well.well_status || well.status || 'UNKNOWN';
  const inactiveYears = Number(well.inactive_years) || 0;

  // Color by Well Group
  const wellGroupColors = {
    'ACTIVE': '#4caf50',        // Green
    'PERMIT': '#607d8b',         // Grey Blue
    'SHUT IN': '#ffc107',        // Yellow
    'TA': '#2196f3',             // Blue
    'PARTIAL PLUG': '#ff9800',   // Orange
    'P&A': '#8d6e63',            // Brown
    'DRY HOLE': '#e91e63'        // Pink
  };

  const domainMin = sizeOpts.domainMin ?? 0;
  const domainMax = sizeOpts.domainMax ?? 8;
  const radius = Math.max(
    4,
    Math.round(
      scaleLinear(
        inactiveYears,
        domainMin,
        domainMax,
        WELL_RADIUS_MIN,
        WELL_RADIUS_MAX
      )
    )
  );
  const color = wellGroupColors[wellGroup] || '#9e9e9e';

  return { wellGroup, color, radius, inactiveYears };
};

// Get lease marker style based on active tab and data
// Size: wellCount (or tab aggregate) mapped into [LEASE_RADIUS_MIN, LEASE_RADIUS_MAX]
// Color: blue shade gradient by same metric vs search-wide max
const getLeaseMarkerStyle = (lease, activeTabType, aggregatedData, sizeOpts = {}) => {
  const domainMin = sizeOpts.domainMin ?? 1;
  const domainMax = sizeOpts.domainMax ?? 20;

  let metric;
  if (aggregatedData && activeTabType !== 'wells') {
    metric = Number(aggregatedData[activeTabType]) || 0;
  } else {
    metric = Number(lease.wellCount ?? lease.wells?.length) || 0;
  }

  const radius = Math.max(
    4,
    Math.round(
      scaleLinear(
        metric,
        domainMin,
        domainMax,
        LEASE_RADIUS_MIN,
        LEASE_RADIUS_MAX
      )
    )
  );
  const color = getLeaseBlueColor(metric, domainMax);

  return { color, radius };
};

const EnhancedMap = ({ 
  searchResults, 
  searchParams, 
  viewMode = 'county', // 'county' | 'lease'
  activeTabType = 'wells', // Which tab is active to determine layer styling
  tileLayer = 'default', // 'default' | 'satellite'
  mapFeatureLayer = 'wells', // 'wells' | 'leases' - which layer is visible
  wellsLabelsVisible = false, // Toggle for well name labels
  leasesLabelsVisible = false, // Toggle for lease name labels
  connectionLinesVisible = true, // Toggle for lease-well links
  horizBoreholeVisible = true, // Toggle for horizontal borehole display
  surveyPolygonsVisible = false, // Toggle for survey polygons
  surveyPolygonsMinZoom = 11, // Minimum zoom for survey polygons
  clustersEnabled = true, // Toggle for marker clustering
  selectionModeEnabled = false, // Toggle for rectangular box selection mode
  onBoxSelection = null, // Callback for box selection completion
  onFreehandSelection = null, // Callback for freehand polygon selection completion
  selectedWells = new Set(), // Currently selected well IDs for visual feedback
  selectedLeases = new Set(), // Currently selected lease keys for visual feedback
  selectionMode = null, // null | 'free' | 'rectangular' - selection mode type
  onWellClick = null, // Callback for well click (for point selection)
  onLeaseClick = null, // Callback for lease click (for point selection)
  onMapReady = null, // Callback to expose map instance to parent
  leaseFlagsMap = null, // Map of leaseKey -> totalFlags for flag display
  countyFlagsMap = null, // Map of countyName -> totalFlags for county popups
  onNavigateToLeaseInSummary = null, // Callback to navigate from map to summary
  navigateToLeaseInMapRef = null, // Ref to register callback for navigating from summary to map
  openLeasePopupRef = null, // Ref to expose function for opening lease popup programmatically
  hideWithoutFlags = false, // Hide counties/leases/wells not tied to selected flags
  flagView = false, // Color/size by flags (county choropleth + lease markers)
  redFlagsCache = null, // Cache of flags for each lease
  selectedFlagDatasets = new Set(['wells', 'production', 'inspections', 'violations', 'severances', 'spills']), // Selected datasets for flag filtering
  onCountiesGeoJSONReady = null, // Callback to expose counties GeoJSON to parent
  onCountyDataReady = null // Callback to expose county data to parent
}) => {
  const stateLayerRef = useRef(null);
  const countiesLayerRef = useRef(null);
  const mapRef = useRef(null);
  const labelMarkersRef = useRef(new Map());
  const [stateGeoJSON, setStateGeoJSON] = useState(null);
  const [countiesGeoJSON, setCountiesGeoJSON] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(6);
  const [mapBounds, setMapBounds] = useState(null);
  const [leaseCentroids, setLeaseCentroids] = useState([]);
  const [leaseAggregatedData, setLeaseAggregatedData] = useState(new Map());
  const [openPopupId, setOpenPopupId] = useState(null); // For lazy popup rendering
  const leaseMarkerRefs = useRef(new Map()); // Store lease marker refs for programmatic popup opening

  // Helper function: Check if marker should be shown based on zoom level (Optimization #5)
  // Only applies when clustering is disabled or at zoom levels where clustering is disabled
  const shouldShowMarkerByZoom = useCallback((lat, lng, zoom, clustersEnabled) => {
    // If clustering is enabled and we're below the clustering disable zoom (12),
    // don't filter - let clusters handle it
    if (clustersEnabled && zoom < 12) {
      return true;
    }

    // zoom < 6: hide all markers
    if (zoom < 6) return false;

    // zoom 6–9: show ~20% of markers (deterministic sampling)
    if (zoom < 9) {
      const seed = Math.abs(Math.floor(lat * 1000 + lng * 1000));
      return (seed % 5) === 0; // Show 1 in 5 (20%)
    }

    // zoom 9–10: show ~50% of markers
    if (zoom < 10) {
      const seed = Math.abs(Math.floor(lat * 1000 + lng * 1000));
      return (seed % 2) === 0; // Show 1 in 2 (50%)
    }

    // zoom 10+: show all markers
    return true;
  }, []);

  // Helper function: Check if marker is in viewport (Optimization #1)
  const isMarkerInViewport = useCallback((lat, lng, bounds) => {
    if (!bounds) return true; // If no bounds, show all (initial load)
    return bounds.contains([lat, lng]);
  }, []);

  // Calculate lease centroids when in lease mode
  useEffect(() => {
    if (viewMode === 'lease' && searchResults?.data?.wells) {
      const centroids = calculateLeaseCentroids(searchResults.data.wells);
      setLeaseCentroids(centroids);
    } else {
      setLeaseCentroids([]);
    }
  }, [viewMode, searchResults]);

  // Expose function to open lease popup programmatically
  useEffect(() => {
    if (openLeasePopupRef) {
      openLeasePopupRef.current = (leaseKey, county = null) => {
        // If county is provided, try county-specific key first, then fall back to leaseKey
        let markerKey = leaseKey;
        if (county) {
          const countyLeaseKey = `${leaseKey}-${county}`;
          const countyMarker = leaseMarkerRefs.current.get(countyLeaseKey);
          if (countyMarker) {
            markerKey = countyLeaseKey;
          }
        }
        
        const marker = leaseMarkerRefs.current.get(markerKey);
        if (!marker) return;
        
        try {
          const leafletMarker = marker.leafletElement || marker;
          if (!leafletMarker) return;
          
          // Fire a click event on the marker - this mimics user click and react-leaflet handles popup opening
          if (leafletMarker.fire) {
            leafletMarker.fire('click');
          }
        } catch (error) {
          logger.warn('Error opening lease popup:', error);
        }
      };
    }
  }, [openLeasePopupRef]);

  // Aggregate data by lease for active tab
  useEffect(() => {
    if (viewMode === 'lease' && leaseCentroids.length > 0 && searchResults?.data) {
      const aggregated = new Map();
      
      leaseCentroids.forEach(lease => {
        // Use countyLeaseKey for unique identification, but leaseKey for matching records
        const markerKey = lease.countyLeaseKey || lease.leaseKey;
        const leaseKey = lease.leaseKey;
        const county = lease.county;
        
        // Count records per tab type for this lease-county combination
        const counts = {
          wells: lease.wellCount || 0,
          production: 0,
          inspections: 0,
          violations: 0,
          severances: 0,
          spills: 0
        };

        // Helper function to check if a record matches the lease and county
        const matchesLeaseAndCounty = (record, recordLeaseKey) => {
          const matchesLease = recordLeaseKey === leaseKey || recordLeaseKey === lease.leaseNumber;
          if (!matchesLease) return false;
          
          // If county is specified, filter by county
          if (county) {
            const recordCounty = (record['County'] || record.county || '').toString().trim();
            return recordCounty === county;
          }
          return true;
        };

        // Count production records
        if (searchResults.data.production) {
          const production = searchResults.data.production || [];
          counts.production = production.filter(p => {
            const pLeaseKey = p['Lease Number']?.includes('-') ? p['Lease Number'] : 
                             `${String(p['District'] || '').padStart(2, '0')}-${p['Lease Number'] || ''}`;
            return matchesLeaseAndCounty(p, pLeaseKey);
          }).length;
        }

        // Count inspections
        if (searchResults.data.inspections) {
          const inspections = searchResults.data.inspections || [];
          counts.inspections = inspections.filter(i => {
            const iLeaseKey = i['Lease Number']?.includes('-') ? i['Lease Number'] : 
                             `${String(i['District'] || '').padStart(2, '0')}-${i['Lease Number'] || ''}`;
            return matchesLeaseAndCounty(i, iLeaseKey);
          }).length;
        }

        // Count violations
        if (searchResults.data.violations) {
          const violations = searchResults.data.violations || [];
          counts.violations = violations.filter(v => {
            const vLeaseKey = v['Lease Number']?.includes('-') ? v['Lease Number'] : 
                             `${String(v['District'] || '').padStart(2, '0')}-${v['Lease Number'] || ''}`;
            return matchesLeaseAndCounty(v, vLeaseKey);
          }).length;
        }

        // Count severances
        if (searchResults.data.severances) {
          const severances = searchResults.data.severances || [];
          counts.severances = severances.filter(s => {
            const sLeaseKey = s['Lease Number']?.includes('-') ? s['Lease Number'] : 
                             `${String(s['District'] || '').padStart(2, '0')}-${s['Lease Number'] || ''}`;
            return matchesLeaseAndCounty(s, sLeaseKey);
          }).length;
        }

        // Count spills
        if (searchResults.data.spills) {
          const spills = searchResults.data.spills || [];
          counts.spills = spills.filter(s => {
            const sLeaseKey = s['Lease Number']?.includes('-') ? s['Lease Number'] : 
                             `${String(s['District'] || '').padStart(2, '0')}-${s['Lease Number'] || ''}`;
            return matchesLeaseAndCounty(s, sLeaseKey);
          }).length;
        }

        // Use countyLeaseKey as the key for aggregated data map
        aggregated.set(markerKey, counts);
      });
      
      setLeaseAggregatedData(aggregated);
    } else {
      setLeaseAggregatedData(new Map());
    }
  }, [viewMode, leaseCentroids, searchResults, activeTabType]);

  // Extract county data for county view mode
  const { countyData, totalRecords } = useMemo(() => {
    if (viewMode !== 'county' || !searchResults?.data) {
      return { countyData: new Map(), totalRecords: 0 };
    }

    const countyStats = new Map();
    const tableTypes = ['wells', 'production', 'inspections', 'violations', 'severances', 'spills'];
    let totalCount = 0;

    tableTypes.forEach(tableType => {
      const tableData = searchResults.data[tableType];
      if (Array.isArray(tableData)) {
        tableData.forEach(record => {
          const county = (record.county || record['County'] || '').toString().trim().toUpperCase();
          
          if (county && county !== '') {
            if (!countyStats.has(county)) {
              countyStats.set(county, {
                name: county,
                counts: {
                  wells: 0,
                  production: 0,
                  inspections: 0,
                  violations: 0,
                  severances: 0,
                  spills: 0
                },
                total: 0
              });
            }
            
            const stats = countyStats.get(county);
            stats.counts[tableType] += 1;
            stats.total += 1;
            totalCount += 1;
          }
        });
      }
    });

    return { countyData: countyStats, totalRecords: totalCount };
  }, [viewMode, searchResults]);

  // Flag totals for Flag View (respects selected datasets)
  const countyFlagMetrics = useMemo(
    () => getCountySelectedFlagMetrics(redFlagsCache, selectedFlagDatasets),
    [redFlagsCache, selectedFlagDatasets]
  );

  // Color scale for counties (shared with Map Layers legend)
  const getCountyColor = (count) => (
    flagView
      ? getCountyFlagColorFromScale(count, countyFlagMetrics.total)
      : getCountyColorFromScale(count, totalRecords)
  );

  const getCountyOpacity = (count) => (
    flagView
      ? getCountyFlagOpacityFromScale(count, countyFlagMetrics.total)
      : getCountyOpacityFromScale(count, totalRecords)
  );

  // Load GeoJSON data
  useEffect(() => {
    fetch('/geodata/us_states.geojson')
      .then(response => response.json())
      .then(data => {
        const texasFeature = data.features.find(
          feature => feature.properties.State_Code === 'TX' || 
                     feature.properties.State_Name === 'TEXAS' ||
                     feature.properties.State_Name === 'TEXAS STATE'
        );
        if (texasFeature) {
          setStateGeoJSON({
            type: 'FeatureCollection',
            features: [texasFeature]
          });
        }
      })
      .catch(error => logger.error('Error loading state GeoJSON:', error));
  }, []);

  useEffect(() => {
    fetch('/geodata/tx_counties.geojson')
      .then(response => response.json())
      .then(data => {
        setCountiesGeoJSON(data);
      })
      .catch(error => logger.error('Error loading counties GeoJSON:', error));
  }, []);


  // Manage county labels based on zoom and view mode
  useEffect(() => {
    if (!mapRef.current) return;

    // Show/hide labels based on zoom level (show at zoom 8 and above)
    const shouldShowLabels = currentZoom >= 8;
    
    labelMarkersRef.current.forEach((marker, labelKey) => {
      // Check if this is a lease view label (prefixed with 'lease-')
      const isLeaseLabel = labelKey.startsWith('lease-');
      const isCountyView = viewMode === 'county';
      const isLeaseView = viewMode === 'lease';
      
      // Show labels for county view (non-lease prefixed) or lease view (lease prefixed)
      const shouldShow = shouldShowLabels && 
                        ((isCountyView && !isLeaseLabel) || (isLeaseView && isLeaseLabel));
      
      if (shouldShow) {
        if (!mapRef.current.hasLayer(marker)) {
          marker.addTo(mapRef.current);
        }
      } else {
        if (mapRef.current.hasLayer(marker)) {
          mapRef.current.removeLayer(marker);
        }
      }
    });
  }, [currentZoom, viewMode, countiesGeoJSON]);

  // Expose counties GeoJSON to parent
  useEffect(() => {
    if (onCountiesGeoJSONReady && countiesGeoJSON) {
      onCountiesGeoJSONReady(countiesGeoJSON);
    }
  }, [onCountiesGeoJSONReady, countiesGeoJSON]);

  // Expose county data to parent
  useEffect(() => {
    if (onCountyDataReady && countyData) {
      onCountyDataReady(countyData);
    }
  }, [onCountyDataReady, countyData]);

  const texasCenter = [31.0, -99.0];
  const texasZoom = 6;

  const stateStyle = {
    fillColor: '#f0f0f0',
    fillOpacity: 0.3,
    color: '#666',
    weight: 2,
    opacity: 0.8
  };

  const onStateEachFeature = (feature, layer) => {
    // Static layer - no interaction
  };

  const getCountyStyle = (recordCount) => {
    const fillColor = getCountyColor(recordCount);
    const fillOpacity = getCountyOpacity(recordCount);
    
    return {
      fillColor,
      fillOpacity,
      color: '#2f4f4f',
      weight: 0.5,
      opacity: 0.9
    };
  };

  // County style for lease view - grey boundaries only (black in satellite view)
  const getLeaseViewCountyStyle = () => {
    const boundaryColor = tileLayer === 'satellite' ? '#000000' : '#2f4f4f';
    return {
      fillColor: 'transparent',
      fillOpacity: 0,
      color: boundaryColor,
      weight: 1.5,
      opacity: 1
    };
  };

  // Handler for counties in lease view
  const onCountiesEachFeatureLease = (feature, layer) => {
    if (viewMode !== 'lease') return;

    const countyName = feature.properties.NAME || feature.properties.name || 'Unknown';
    
    // Apply black boundary style only
    const style = getLeaseViewCountyStyle();
    layer.setStyle(style);
    
    // No popups/tooltips
    layer.unbindPopup();
    
    // Create label marker for this county (same as county view)
    const bounds = layer.getBounds();
    const center = bounds.getCenter();
    
    const estimatedWidth = countyName.length * 7;
    const estimatedHeight = 15;
    
    // Label color: always dark for lease view
    const labelColor = '#333';
    
    const labelDiv = L.divIcon({
      className: 'county-label',
      html: `<div style="
        font-size: 11px;
        font-weight: 600;
        color: ${labelColor};
        text-align: center;
        white-space: nowrap;
        pointer-events: none;
        text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.8);
        width: ${estimatedWidth}px;
        height: ${estimatedHeight}px;
        display: flex;
        align-items: center;
        justify-content: center;
      ">${countyName}</div>`,
      iconSize: [estimatedWidth, estimatedHeight],
      iconAnchor: [estimatedWidth / 2, estimatedHeight / 2]
    });
    
    const labelMarker = L.marker(center, { icon: labelDiv, interactive: false });
    
    // Store label marker reference (with a lease prefix to differentiate)
    const labelKey = `lease-${countyName}`;
    if (!labelMarkersRef.current.has(labelKey)) {
      labelMarkersRef.current.set(labelKey, labelMarker);
      // Add to map if zoom is high enough and we're in lease view
      if (mapRef.current && viewMode === 'lease' && currentZoom >= 8) {
        labelMarker.addTo(mapRef.current);
      }
    }
  };

  const onCountiesEachFeature = (feature, layer) => {
    if (viewMode !== 'county') return;

    const countyName = feature.properties.NAME || feature.properties.name || 'Unknown';
    const normalizedCountyName = countyName.toUpperCase();
    const stats = countyData.get(normalizedCountyName);
    let recordCount = stats ? stats.total : 0;
    // When hiding without flags, treat counties with no matching lease flags as empty
    if (hideWithoutFlags && redFlagsCache && recordCount > 0) {
      if (!countyHasSelectedFlags(redFlagsCache, normalizedCountyName, selectedFlagDatasets)) {
        recordCount = 0;
      }
    }
    const countyFlagCount = countyFlagMetrics.byCounty.get(normalizedCountyName) || 0;
    const choroplethCount = flagView ? countyFlagCount : recordCount;
    const hasData = choroplethCount > 0;
    
    // Create label marker for this county (centered on the county)
    const bounds = layer.getBounds();
    const center = bounds.getCenter();
    
    // Estimate text width for proper centering (rough approximation: 7px per character)
    const estimatedWidth = countyName.length * 7;
    const estimatedHeight = 15;
    
    // Label color: gray for counties without data, dark for counties with data
    const labelColor = hasData ? '#333' : '#888';
    
    const labelDiv = L.divIcon({
      className: 'county-label',
      html: `<div style="
        font-size: 11px;
        font-weight: 600;
        color: ${labelColor};
        text-align: center;
        white-space: nowrap;
        pointer-events: none;
        text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.8);
        width: ${estimatedWidth}px;
        height: ${estimatedHeight}px;
        display: flex;
        align-items: center;
        justify-content: center;
      ">${countyName}</div>`,
      iconSize: [estimatedWidth, estimatedHeight],
      iconAnchor: [estimatedWidth / 2, estimatedHeight / 2]
    });
    
    const labelMarker = L.marker(center, { icon: labelDiv, interactive: false });
    
    // Store label marker reference
    if (!labelMarkersRef.current.has(countyName)) {
      labelMarkersRef.current.set(countyName, labelMarker);
    }
    
    if (hasData || recordCount > 0) {
      const counts = stats?.counts || {};
      const tableTypes = ['wells', 'production', 'inspections', 'violations', 'severances', 'spills'];
      const searchedTables = tableTypes.filter(tableType => {
        if (searchParams) {
          const paramKey = `search_${tableType}`;
          return searchParams[paramKey] === true;
        }
        return searchResults?.data?.[tableType] !== undefined;
      });
      
      const tableDisplay = searchedTables.map(tableType => {
        const label = tableType.charAt(0).toUpperCase() + tableType.slice(1);
        const count = counts[tableType] || 0;
        return `${label}: ${count.toLocaleString()}`;
      });
      
      let popupContent = `<strong>${countyName} County</strong>`;
      popupContent += `<br/><br/><strong>Total Records: ${recordCount.toLocaleString()}</strong>`;
      if (tableDisplay.length > 0) {
        popupContent += `<br/><br/>${tableDisplay.join('<br/>')}`;
      }

      const popupFlagCount = flagView
        ? countyFlagCount
        : (countyFlagsMap?.get(normalizedCountyName) || countyFlagCount);
      if (popupFlagCount > 0) {
        popupContent += `<br/><br/>Flags: ${getFlagIconHTML(popupFlagCount)}`;
      }
      
      layer.bindPopup(popupContent);
    }
    
    const baseStyle = getCountyStyle(choroplethCount);
    layer.setStyle(baseStyle);
  };

  // Get wells data for lease view
  const wellsData = useMemo(() => {
    return viewMode === 'lease' ? (searchResults?.data?.wells || []) : [];
  }, [viewMode, searchResults?.data?.wells]);

  // Stable min/max for well marker sizing (inactive years across search results)
  const wellSizeDomain = useMemo(() => {
    const wells = searchResults?.data?.wells || [];
    let min = Infinity;
    let max = -Infinity;
    wells.forEach((well) => {
      const years = Number(well.inactive_years) || 0;
      if (years < min) min = years;
      if (years > max) max = years;
    });
    return normalizeDomain(min, max);
  }, [searchResults?.data?.wells]);

  // Stable min/max for lease marker sizing (well count, tab aggregate, or flags in Flag View)
  const leaseSizeDomain = useMemo(() => {
    if (leaseCentroids.length === 0) {
      return { min: 1, max: 20 };
    }
    let min = Infinity;
    let max = -Infinity;
    leaseCentroids.forEach((lease) => {
      let metric;
      if (flagView) {
        metric = getLeaseSelectedFlagCount(
          redFlagsCache,
          lease.leaseKey,
          lease.county,
          selectedFlagDatasets
        );
      } else {
        const markerKey = lease.countyLeaseKey || lease.leaseKey;
        const aggregated = leaseAggregatedData.get(markerKey) || {};
        if (activeTabType !== 'wells' && aggregated[activeTabType] != null) {
          metric = Number(aggregated[activeTabType]) || 0;
        } else {
          metric = Number(lease.wellCount ?? lease.wells?.length) || 0;
        }
      }
      if (metric < min) min = metric;
      if (metric > max) max = metric;
    });
    return normalizeDomain(min, max);
  }, [leaseCentroids, leaseAggregatedData, activeTabType, flagView, redFlagsCache, selectedFlagDatasets]);

  // Memoized well markers with all optimizations (Optimization #3, #1, #5, #6)
  const wellMarkers = useMemo(() => {
    if (mapFeatureLayer !== 'wells' || wellsData.length === 0) {
      return [];
    }

    // Well markers - grouped by API number and location
    const wellsByLocation = new Map();
    
    wellsData
      .filter(well => {
        const lat = parseFloat(well['Latitude'] || well.latitude || well.lat);
        const lng = parseFloat(well['Longitude'] || well.longitude || well.lng || well.lon);
        return !isNaN(lat) && !isNaN(lng);
      })
      .forEach(well => {
        const lat = parseFloat(well['Latitude'] || well.latitude || well.lat);
        const lng = parseFloat(well['Longitude'] || well.longitude || well.lng || well.lon);
        const apiNumber = well['API Number'] || well.api_number || '';
        
        // Create a key based on API number and rounded coordinates (to handle floating point differences)
        const locationKey = `${apiNumber}-${lat.toFixed(6)}-${lng.toFixed(6)}`;
        
        if (!wellsByLocation.has(locationKey)) {
          wellsByLocation.set(locationKey, {
            lat,
            lng,
            apiNumber,
            wells: []
          });
        }
        
        wellsByLocation.get(locationKey).wells.push(well);
      });
    
    // Apply optimizations: viewport culling and zoom-based filtering
    const filteredLocationGroups = Array.from(wellsByLocation.values()).filter(locationGroup => {
      const { lat, lng, wells } = locationGroup;
      
      // Optimization #1: Viewport culling
      // If horizBorehole is visible, check both surface and bottomhole locations
      if (horizBoreholeVisible) {
        // Check if surface location is in viewport
        const surfaceInViewport = isMarkerInViewport(lat, lng, mapBounds);
        
        // Check if any well in this group has bottomhole coordinates in viewport
        let bottomholeInViewport = false;
        for (const well of wells) {
          const latB = parseFloat(well.latitude_b);
          const lngB = parseFloat(well.longitude_b);
          if (!isNaN(latB) && !isNaN(lngB) && (latB !== lat || lngB !== lng)) {
            if (isMarkerInViewport(latB, lngB, mapBounds)) {
              bottomholeInViewport = true;
              break;
            }
          }
        }
        
        // Include if either surface or bottomhole is in viewport
        if (!surfaceInViewport && !bottomholeInViewport) {
          return false;
        }
      } else {
        // Normal viewport culling when bottomhole is not visible
      if (!isMarkerInViewport(lat, lng, mapBounds)) {
        return false;
        }
      }
      
      // Optimization #5: Zoom-based marker reduction
      // Only filter when clustering is disabled or at zoom >= 12 (where clustering is disabled)
      if (!shouldShowMarkerByZoom(lat, lng, currentZoom, clustersEnabled)) {
        return false;
      }

      // Hide wells whose lease has no matching flags
      if (hideWithoutFlags && redFlagsCache) {
        const well = wells[0];
        if (!well) return false;
        const { leaseKey, county } = getWellLeaseIdentity(well, generateLeaseKey);
        if (!leaseHasSelectedFlags(redFlagsCache, leaseKey, county, selectedFlagDatasets)) {
          return false;
        }
      }
      
      return true;
    });
    
    // Render markers for each filtered grouped location
    return filteredLocationGroups.map((locationGroup, idx) => {
      const { lat, lng, apiNumber, wells } = locationGroup;
      
      // Check if any well in this group is selected
      const primaryWell = wells[0];
      // Check if any well in the group is selected by comparing API numbers
      let isSelected = false;
      for (const well of wells) {
        const wellId = well['API Number'] || well.api_number || well.api;
        const wellIndex = searchResults?.data?.wells?.indexOf(well) ?? idx;
        const checkId = wellId || `well-${wellIndex}`;
        if (selectedWells.has(checkId)) {
          isSelected = true;
          break;
        }
      }
      
      // Get unique well groups
      const wellGroups = [...new Set(wells.map(w => 
        (w['Well Group'] || w.well_group || 'UNKNOWN').toUpperCase().trim()
      ))];
      const hasMultipleGroups = wellGroups.length > 1;
      
      // Get the primary well group (first one, or use a combined icon if multiple)
      const primaryStyle = getWellMarkerStyle(primaryWell, {
        domainMin: wellSizeDomain.min,
        domainMax: wellSizeDomain.max,
      });
      
      // Use a special icon if there are multiple well groups
      // If selected, add a border/glow effect (Optimization #2: Icon caching is handled in createWellMarkerIcon)
      const icon = hasMultipleGroups 
        ? createWellMarkerIcon('MULTI', primaryStyle.color, primaryStyle.radius, wellGroups.length, isSelected)
        : createWellMarkerIcon(primaryStyle.wellGroup, primaryStyle.color, primaryStyle.radius, 1, isSelected);
      
      // Get primary well ID for click handler
      const primaryWellId = primaryWell['API Number'] || primaryWell.api_number || primaryWell.api || `well-${idx}`;
      const popupId = `well-${idx}-${apiNumber}`;

      return (
        <React.Fragment key={`well-group-${idx}-${apiNumber}`}>
          <Marker
            position={[lat, lng]}
            icon={icon}
            eventHandlers={selectionMode === 'free' && onWellClick ? {
              click: () => {
                onWellClick(primaryWellId, primaryWell);
              }
            } : undefined}
          >
            {selectionMode === null && (
              <Popup
                maxWidth={300}
                eventHandlers={{
                  add: () => setOpenPopupId(popupId),
                  remove: () => setOpenPopupId(prev => prev === popupId ? null : prev)
                }}
              >
              {/* Optimization #6: Lazy popup rendering - only render content when popup is open */}
              {openPopupId === popupId && (
                <div style={{ fontSize: 'clamp(0.6rem, 2vw, 0.7rem)' }}>
                  {wells.map((well, wellIdx) => {
                    // Get well name
                    const wellNameRaw = well['Well Name'] || well.well_name || 'Unknown Well';
                    const wellName = wellNameRaw === 'Unknown Well' ? 'UNKNOWN WELL' : wellNameRaw;
                    
                    // Get operator name
                    const operatorName = well['Operator Name'] || well.operator_name || well['Operator'] || well.operator || null;
                    
                    // Get well group and type
                    const wellGroup = well['Well Group'] || well.well_group || 'Unknown';
                    const wellType = well['Well Type'] || well.well_type || 'Unknown';
                    const wellDesc = well['Well Description'] || well.well_description || 'Unknown';
                    
                    // Get API number
                    const apiNumber = well['API Number'] || well.api_number || well.api || null;
                    
                    // Get lease information
                    const district = well['District'] || well.district || '00';
                    const leaseNumber = well['Lease Number'] || well.lease_number || null;
                    // const leaseNameRaw = well['Lease Name'] || well.lease_name || 'Unknown Lease';
                    // const leaseName = leaseNameRaw === 'Unknown Lease' ? 'UNKNOWN LEASE' : leaseNameRaw;
                    const displayLeaseNumber = (leaseNumber && leaseNumber !== 'Unknown Lease' && leaseNumber !== '') 
                      ? `${String(district).padStart(2, '0')}-${leaseNumber}`.toUpperCase()
                      : `${String(district).padStart(2, '0')}-00000`;
                    
                    // Helper function to format dates - return empty string if null/undefined/empty (no "NO DATA")
                    const formatDate = (value) => {
                      if (value === null || value === undefined || value === '') {
                        return '';
                      }
                      return value;
                    };
                    
                    // Get dates
                    const permitApprovedDate = formatDate(
                      well['Permit Approved Date'] || well.permit_approved_date || well['Permit Date'] || well.permit_date
                    );
                    const drillDate = formatDate(
                      well['Drill Date'] || well.drill_date
                    );
                    const completionDate = formatDate(
                      well['Completion Date'] || well.completion_date
                    );
                    const plugDate = formatDate(
                      well['Plug Date'] || well.plug_date || well['Plugged Date'] || well.plugged_date
                    );
                    
                    // Get depths and format for display
                    const trueVerticalDepthRaw = well['True Vertical Depth'] || well.true_vertical_depth || well['TVD'] || well.tvd;
                    const measuredDepthRaw = well['Measured Depth'] || well.measured_depth || well['MD'] || well.md;
                    
                    // Format depth values with commas if they're numbers
                    const formatDepth = (value) => {
                      if (value === null || value === undefined || value === '') {
                        return null;
                      }
                      const numValue = typeof value === 'number' ? value : parseFloat(value);
                      if (!isNaN(numValue)) {
                        return numValue.toLocaleString();
                      }
                      return value;
                    };
                    
                    const tvdFormatted = formatDepth(trueVerticalDepthRaw);
                    const mdFormatted = formatDepth(measuredDepthRaw);
                    
                    // Combine into single line: "TVD / MD: 10,702 / 21,176" or "100 / " if one missing, or null if both missing
                    const depthDisplay = (tvdFormatted && mdFormatted) 
                      ? `${tvdFormatted} / ${mdFormatted}`
                      : (tvdFormatted ? `${tvdFormatted} / ` : (mdFormatted ? ` / ${mdFormatted}` : null));
                    
                    // Get API Depth
                    const apiDepthRaw = well['API_DEPTH'] || well['API Depth'] || well.api_depth || well['Total Depth'] || well.total_depth;
                    const apiDepthFormatted = formatDepth(apiDepthRaw);
                    const apiDepthDisplay = apiDepthFormatted || null;
                    
                    // Get plugging cost information
                    const pluggingCostRaw = well['PLUGGING_COST'] || well['plugging_cost'] || well['Plugging Cost'] || null;
                    const pluggingCostFtRaw = well['PLUGGING_COST_FT'] || well['plugging_cost_ft'] || well['Plugging Cost FT'] || null;
                    
                    // Format plugging cost
                    let pluggingCostDisplay = null;
                    if (pluggingCostRaw !== null && pluggingCostRaw !== undefined && pluggingCostRaw !== '' && 
                        typeof pluggingCostRaw === 'number' && !isNaN(pluggingCostRaw)) {
                      const costFormatted = pluggingCostRaw.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                      const costFtFormatted = (pluggingCostFtRaw !== null && pluggingCostFtRaw !== undefined && 
                                             typeof pluggingCostFtRaw === 'number' && !isNaN(pluggingCostFtRaw))
                        ? pluggingCostFtRaw.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
                        : null;
                      if (costFtFormatted) {
                        pluggingCostDisplay = `$${costFormatted} ($${costFtFormatted}/FT)`;
                      } else {
                        pluggingCostDisplay = `$${costFormatted}`;
                      }
                    } else if (pluggingCostRaw === 'SEE OTHER API RECORD' || String(pluggingCostRaw).includes('SEE OTHER')) {
                      pluggingCostDisplay = 'SEE OTHER API RECORD';
                    }
                    
                    // Determine if API DEPTH was missing (5000 default was used)
                    // If apiDepthDisplay is null but pluggingCostDisplay exists, the 5000 default was likely used
                    const usesDefaultDepth = !apiDepthDisplay && pluggingCostDisplay !== null;
                    
                    // Check if inactive_years is null/undefined
                    const inactiveYearsRaw = well.inactive_years;
                    // Return null if inactive_years is null/undefined (will hide the line)
                    const inactiveYearsDisplay = (inactiveYearsRaw === null || inactiveYearsRaw === undefined) 
                      ? null
                      : inactiveYearsRaw;

                    // Check if there's any date or inactive years data
                    const hasDateData = permitApprovedDate || drillDate || completionDate || plugDate || 
                                      (inactiveYearsDisplay !== null && inactiveYearsDisplay !== undefined);
                    
                    // Check if there's any depth/cost data
                    const hasDepthCostData = depthDisplay !== null || apiDepthDisplay || pluggingCostDisplay;

                    return (
                      <div key={`popup-well-${wellIdx}`} style={{ marginBottom: wellIdx < wells.length - 1 ? '16px' : '0', paddingBottom: wellIdx < wells.length - 1 ? '16px' : '0', borderBottom: wellIdx < wells.length - 1 ? '1px solid #ddd' : 'none' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '1em', marginBottom: '8px' }}>{wellName}</div>
                        <div style={{ fontStyle: 'italic', color: '#666', marginBottom: '12px' }}>{operatorName}</div>
                        
                        <div style={{ marginBottom: '4px' }}>
                          <span style={{ fontWeight: '600', color: '#555' }}>Well Group:</span>
                          <span style={{ marginLeft: '8px' }}>{wellGroup}</span>
                        </div>
                        <div style={{ marginBottom: '4px' }}>
                          <span style={{ fontWeight: '600', color: '#555' }}>Well Type:</span>
                          <span style={{ marginLeft: '8px' }}>{wellType}</span>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{ fontWeight: '600', color: '#555' }}>Description:</span>
                          <span style={{ marginLeft: '8px' }}>{wellDesc}</span>
                        </div>
                        
                        <hr style={{ margin: '10px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                        
                        <div style={{ marginBottom: '4px' }}>
                          <span style={{ fontWeight: '600', color: '#555' }}>API #:</span>
                          <span style={{ marginLeft: '8px' }}>{apiNumber || 'NO DATA'}</span>
                        </div>
                        {/* <div style={{ marginBottom: '4px' }}>
                          <span style={{ fontWeight: '600', color: '#555' }}>Lease Name:</span>
                          <span style={{ marginLeft: '8px' }}>{leaseName}</span>
                        </div> */}
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{ fontWeight: '600', color: '#555' }}>Lease #:</span>
                          <span style={{ marginLeft: '8px' }}>{displayLeaseNumber}</span>
                        </div>
                        
                        {hasDateData && <hr style={{ margin: '10px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />}
                        {permitApprovedDate && (
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ fontWeight: '600', color: '#555' }}>Permit Date:</span>
                            <span style={{ marginLeft: '8px' }}>{permitApprovedDate}</span>
                          </div>
                        )}
                        {drillDate && (
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ fontWeight: '600', color: '#555' }}>Drill Date:</span>
                            <span style={{ marginLeft: '8px' }}>{drillDate}</span>
                          </div>
                        )}
                        {completionDate && (
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ fontWeight: '600', color: '#555' }}>Completion Date:</span>
                            <span style={{ marginLeft: '8px' }}>{completionDate}</span>
                          </div>
                        )}
                        {plugDate && (
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ fontWeight: '600', color: '#555' }}>Plug Date:</span>
                            <span style={{ marginLeft: '8px' }}>{plugDate}</span>
                          </div>
                        )}
                        {inactiveYearsDisplay !== null && inactiveYearsDisplay !== undefined && (
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ fontWeight: '600', color: '#555' }}>Inactive Years:</span>
                            <span style={{ marginLeft: '8px' }}>{inactiveYearsDisplay}</span>
                          </div>
                        )}
                        
                        {hasDepthCostData && <hr style={{ margin: '10px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />}
                        {depthDisplay !== null && (
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ fontWeight: '600', color: '#555' }}>TVD / MD:</span>
                            <span style={{ marginLeft: '8px' }}>{depthDisplay}</span>
                          </div>
                        )}
                        {apiDepthDisplay && (
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ fontWeight: '600', color: '#555' }}>API (Total) Depth:</span>
                            <span style={{ marginLeft: '8px' }}>{apiDepthDisplay}</span>
                          </div>
                        )}
                        {pluggingCostDisplay && (
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ fontWeight: '600', color: '#555' }}>Est ARO:</span>
                            <span style={{ marginLeft: '8px' }}>{pluggingCostDisplay}{usesDefaultDepth ? '*' : ''}</span>
                          </div>
                        )}
                        
                        {/* View Report Card link - only show once per well group, at the end of the last well */}
                        {wellIdx === wells.length - 1 && onNavigateToLeaseInSummary && (() => {
                          // Generate lease key from well data
                          const wellDistrict = well['District'] || well.district || '00';
                          const wellLeaseNumber = well['Lease Number'] || well.lease_number || null;
                          const wellLeaseName = well['Lease Name'] || well.lease_name || null;
                          const wellCounty = well['County'] || well.county || null;
                          const leaseKey = generateLeaseKey(wellDistrict, wellLeaseNumber, wellLeaseName);
                          
                          // Try to find the lease from leaseCentroids to get full lease data
                          let leaseData = null;
                          if (leaseCentroids && leaseCentroids.length > 0) {
                            leaseData = leaseCentroids.find(lease => lease.leaseKey === leaseKey);
                          }
                          
                          // Create minimal lease object if not found
                          if (!leaseData) {
                            leaseData = {
                              leaseKey: leaseKey,
                              district: wellDistrict,
                              leaseNumber: wellLeaseNumber,
                              leaseName: wellLeaseName,
                              county: wellCounty
                            };
                          }
                          
                          return (
                            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e0e0e0' }}>
                              <Link
                                component="button"
                                variant="body2"
                                onClick={() => {
                                  if (onNavigateToLeaseInSummary) {
                                    onNavigateToLeaseInSummary(leaseKey, leaseData);
                                  }
                                }}
                                sx={{ 
                                  fontSize: 'clamp(0.6rem, 2vw, 0.7rem)',
                                  textDecoration: 'none',
                                  color: '#1976d2',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                View Report Card
                              </Link>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              )}
              </Popup>
            )}
          </Marker>
          
          {/* Bottomhole marker and line - only if well has bottomhole coordinates, toggle is enabled, coordinates differ from surface, and clusters are off or zoom is when clusters are off */}
          {horizBoreholeVisible && (!clustersEnabled || currentZoom >= 13) && (() => {
            const latB = parseFloat(primaryWell.latitude_b);
            const lngB = parseFloat(primaryWell.longitude_b);
            
            // Only display if bottomhole coordinates are valid and different from surface coordinates
            if (!isNaN(latB) && !isNaN(lngB) && (latB !== lat || lngB !== lng)) {
              const bottomholeColor = primaryStyle.color;
              
              return (
                <React.Fragment key={`bottomhole-${idx}-${apiNumber}`}>
                  {/* Solid line connecting surface to bottomhole */}
                  <Polyline
                    positions={[[lat, lng], [latB, lngB]]}
                    pathOptions={{
                      color: bottomholeColor,
                      weight: 3
                    }}
                  />
                  {/* Small square marker at bottomhole location */}
                  <Marker
                    position={[latB, lngB]}
                    icon={createBottomholeIcon('#000000')}
                  />
                </React.Fragment>
              );
            }
            return null;
          })()}
        </React.Fragment>
      );
    });
  }, [
    mapFeatureLayer,
    wellsData,
    selectedWells,
    selectionMode,
    currentZoom,
    mapBounds,
    isMarkerInViewport,
    shouldShowMarkerByZoom,
    clustersEnabled,
    searchResults?.data?.wells,
    onWellClick,
    openPopupId,
    horizBoreholeVisible,
    leaseCentroids,
    onNavigateToLeaseInSummary,
    hideWithoutFlags,
    redFlagsCache,
    selectedFlagDatasets,
    wellSizeDomain,
  ]);

  // Helper: when hideWithoutFlags is off (or flags not ready), everything is visible
  const hasFlagsFromSelectedDatasets = useCallback((leaseKey, county = null) => {
    if (!hideWithoutFlags || !redFlagsCache) {
      return true;
    }
    return leaseHasSelectedFlags(redFlagsCache, leaseKey, county, selectedFlagDatasets);
  }, [redFlagsCache, hideWithoutFlags, selectedFlagDatasets]);

  // Memoized lease markers with all optimizations
  const leaseMarkers = useMemo(() => {
    if (mapFeatureLayer !== 'leases' || leaseCentroids.length === 0) {
      return [];
    }

    // Apply optimizations: viewport culling and zoom-based filtering
    const filteredLeases = leaseCentroids.filter(lease => {
      const [lat, lng] = lease.centroid;
      
      // Optimization #1: Viewport culling
      if (!isMarkerInViewport(lat, lng, mapBounds)) {
        return false;
      }
      
      // Optimization #5: Zoom-based marker reduction
      // Only filter when clustering is disabled or at zoom >= 12 (where clustering is disabled)
      if (!shouldShowMarkerByZoom(lat, lng, currentZoom, clustersEnabled)) {
        return false;
      }
      
      // Filter out leases without flags from selected datasets if toggle is enabled
      if (hideWithoutFlags) {
        if (!hasFlagsFromSelectedDatasets(lease.leaseKey, lease.county)) {
          return false;
        }
      }
      
      return true;
    });

    return filteredLeases.map((lease) => {
      // Use countyLeaseKey for unique marker identification, but leaseKey for aggregated data lookup
      const markerKey = lease.countyLeaseKey || lease.leaseKey;
      const aggregated = leaseAggregatedData.get(markerKey) || {};
      const selectedFlagCount = getLeaseSelectedFlagCount(
        redFlagsCache,
        lease.leaseKey,
        lease.county,
        selectedFlagDatasets
      );
      const wellCount = lease.wellCount || lease.wells.length;
      const style = flagView
        ? {
            color: getLeaseFlagColor(
              selectedFlagCount,
              leaseSizeDomain.max
            ),
            radius: Math.max(
              4,
              Math.round(
                scaleLinear(
                  selectedFlagCount,
                  leaseSizeDomain.min,
                  leaseSizeDomain.max,
                  LEASE_RADIUS_MIN,
                  LEASE_RADIUS_MAX
                )
              )
            ),
          }
        : getLeaseMarkerStyle(lease, activeTabType, aggregated, {
            domainMin: leaseSizeDomain.min,
            domainMax: leaseSizeDomain.max,
          });
      const tabLabel = activeTabType !== 'wells' ? activeTabType.charAt(0).toUpperCase() + activeTabType.slice(1) : null;
      // Use size instead of radius for square markers
      const markerSize = Math.max(style.radius * 2, 10);
      const isSelected = selectedLeases.has(lease.leaseKey);
      // Get flag count for this lease (using county-lease key for accuracy)
      // County is always available in lease objects, so we can always use county-lease key
      const countyLeaseKey = `${lease.county}-${lease.leaseKey}`;
      const flagCount = flagView
        ? selectedFlagCount
        : (leaseFlagsMap?.get(countyLeaseKey) || selectedFlagCount);
      // Flag View: show flag count in diamond, no corner badge; else well count + badge when flags exist
      const icon = createLeaseMarkerIcon(
        style.color,
        markerSize,
        flagView ? selectedFlagCount : wellCount,
        1,
        isSelected,
        flagView ? 0 : flagCount
      );
      const popupId = `lease-${markerKey}`;

      return (
        <React.Fragment key={`lease-${markerKey}`}>
          <Marker
            ref={(marker) => {
              if (marker) {
                leaseMarkerRefs.current.set(markerKey, marker);
              } else {
                leaseMarkerRefs.current.delete(markerKey);
              }
            }}
            position={lease.centroid}
            icon={icon}
            zIndexOffset={1000}
            eventHandlers={selectionMode === 'free' && onLeaseClick ? {
              click: () => {
                onLeaseClick(lease.leaseKey, lease);
              }
            } : undefined}
          >
            {selectionMode === null && (
              <Popup
                eventHandlers={{
                  add: () => setOpenPopupId(popupId),
                  remove: () => setOpenPopupId(prev => prev === popupId ? null : prev)
                }}
              >
              {/* Optimization #6: Lazy popup rendering */}
              {openPopupId === popupId && (
                <div style={{ fontSize: 'clamp(0.625rem, 2vw, 0.75rem)' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '1em', marginBottom: '12px' }}>{lease.leaseName}</div>
                  
                  {/* Show county if lease spans multiple counties */}
                  {lease.county && (
                    <div style={{ marginBottom: '4px' }}>
                      <span style={{ fontWeight: '600', color: '#555' }}>County:</span>
                      <span style={{ marginLeft: '8px' }}>{lease.county}</span>
                    </div>
                  )}
                  
                  {/* Format lease number for display (clean format: 08-12345 or 08-00000) */}
                  {(() => {
                    const dist = lease.district || '00';
                    const num = lease.leaseNumber || null;
                    const displayLeaseNumber = (num && num !== 'Unknown Lease' && num !== '') 
                      ? `${String(dist).padStart(2, '0')}-${num}`.toUpperCase()
                      : `${String(dist).padStart(2, '0')}-00000`;
                    return (
                      <div style={{ marginBottom: '4px' }}>
                        <span style={{ fontWeight: '600', color: '#555' }}>Lease #:</span>
                        <span style={{ marginLeft: '8px' }}>{displayLeaseNumber}</span>
                      </div>
                    );
                  })()}
                  
                  <div style={{ marginBottom: '4px' }}>
                    <span style={{ fontWeight: '600', color: '#555' }}>Wells:</span>
                    <span style={{ marginLeft: '8px' }}>{lease.wellCount || lease.wells.length}</span>
                  </div>
                  
                  {tabLabel && aggregated[activeTabType] !== undefined && aggregated[activeTabType] !== null && aggregated[activeTabType] > 0 && (
                    <div style={{ marginBottom: '4px' }}>
                      <span style={{ fontWeight: '600', color: '#555' }}>{tabLabel}:</span>
                      <span style={{ marginLeft: '8px' }}>{aggregated[activeTabType].toLocaleString()}</span>
                    </div>
                  )}
                  
                  {flagCount > 0 && (
                    <div style={{ marginBottom: '4px' }}>
                      <span style={{ fontWeight: '600', color: '#555' }}>Flags:</span>
                      <span style={{ marginLeft: '8px' }} dangerouslySetInnerHTML={{ __html: getFlagIconHTML(flagCount) }} />
                    </div>
                  )}
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e0e0e0' }}>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => {
                        if (onNavigateToLeaseInSummary) {
                          onNavigateToLeaseInSummary(lease.leaseKey, lease);
                        }
                        }}
                        sx={{ 
                          fontSize: 'clamp(0.625rem, 2vw, 0.75rem)',
                          textDecoration: 'none',
                        color: onNavigateToLeaseInSummary ? '#1976d2' : '#999',
                        cursor: onNavigateToLeaseInSummary ? 'pointer' : 'default',
                          '&:hover': {
                          textDecoration: onNavigateToLeaseInSummary ? 'underline' : 'none'
                          }
                        }}
                      >
                        View Report Card
                      </Link>
                    </div>
                </div>
              )}
              </Popup>
            )}
          </Marker>
        </React.Fragment>
      );
    });
  }, [
    mapFeatureLayer,
    leaseCentroids,
    leaseAggregatedData,
    activeTabType,
    selectedLeases,
    selectionMode,
    currentZoom,
    mapBounds,
    isMarkerInViewport,
    shouldShowMarkerByZoom,
    clustersEnabled,
    onLeaseClick,
    openPopupId,
    leaseFlagsMap,
    onNavigateToLeaseInSummary,
    hideWithoutFlags,
    hasFlagsFromSelectedDatasets,
    leaseSizeDomain,
    flagView,
    redFlagsCache,
    selectedFlagDatasets,
  ]);

  if (!stateGeoJSON && !countiesGeoJSON && viewMode === 'county') {
    return (
      <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
        Loading map...
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Custom styles for zoom controls positioning */}
      <style>{`
        .leaflet-bottom.leaflet-right {
          bottom: 16px !important;
          right: 16px !important;
        }
        .leaflet-control-zoom {
          margin: 0 !important;
          bottom: 8px !important;
        }
      `}</style>
      <MapContainer
        ref={mapRef}
        center={texasCenter}
        zoom={texasZoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        zoomControl={false}
        whenCreated={(map) => {
          mapRef.current = map;
          setCurrentZoom(map.getZoom());
        }}
      >
        <ZoomControl position="bottomright" />
        <ZoomTracker onZoomChange={setCurrentZoom} onBoundsChange={setMapBounds} />
        <MapInstanceExposer onMapReady={onMapReady} />
        <MapBoundsAdjuster 
          viewMode={viewMode} 
          leaseCentroids={leaseCentroids}
          wellsData={wellsData}
        />
        
        {/* Box Selector for drag-to-select */}
        {selectionModeEnabled && onBoxSelection && (
          <BoxSelector enabled={selectionModeEnabled} onSelectionComplete={onBoxSelection} />
        )}
        {selectionMode === 'freehand' && onFreehandSelection && (
          <FreehandSelector enabled={selectionMode === 'freehand'} onSelectionComplete={onFreehandSelection} />
        )}
        
        {tileLayer === 'satellite' ? (
          <TileLayer
            attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            detectRetina={true}
          />
        ) : (
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            detectRetina={true}
          />
        )}
        
        {/* Survey Polygons Layer - Loads county tiles dynamically based on viewport */}
        <CountyTiles 
          visible={surveyPolygonsVisible}
          minZoom={surveyPolygonsMinZoom}
          tileLayer={tileLayer}
          style={{
            fillColor: 'transparent',
            fillOpacity: 0,
            color: tileLayer === 'satellite' ? '#000000' : '#5a7a7a', // Black in satellite, gray in default
            weight: 1,
            opacity: 0.9
          }}
        />
        
        {/* County View Layers */}
        {viewMode === 'county' && (
          <>
            {stateGeoJSON && (
              <GeoJSON
                ref={stateLayerRef}
                data={stateGeoJSON}
                style={stateStyle}
                onEachFeature={onStateEachFeature}
              />
            )}
            
            {countiesGeoJSON && (
              <GeoJSON
                key={`county-${countyData.size}-${hideWithoutFlags}-${flagView}-${countyFlagMetrics.total}-${[...selectedFlagDatasets].sort().join(',')}`}
                ref={countiesLayerRef}
                data={countiesGeoJSON}
                onEachFeature={onCountiesEachFeature}
              />
            )}
          </>
        )}

        {/* Lease View Layers */}
        {viewMode === 'lease' && (
          <>
            {/* County boundaries - black outlines only (black in satellite, gray in default) */}
            {countiesGeoJSON && (
              <GeoJSON
                key={`lease-counties-${countyData.size}-${tileLayer}`}
                data={countiesGeoJSON}
                onEachFeature={onCountiesEachFeatureLease}
              />
            )}
            
            {/* Lease-well links from lease centroids to wells - visible when leases layer is selected, lease-well links are enabled, and clusters disappear (zoom 13+) */}
            {mapFeatureLayer === 'leases' && connectionLinesVisible && currentZoom >= 13 && leaseCentroids
              .filter((lease) => hasFlagsFromSelectedDatasets(lease.leaseKey, lease.county))
              .map((lease) => {
              const connections = createLeaseWellConnections(lease);
              return connections.map((connection, idx) => {
                const wellPosition = connection.geometry.coordinates[1]; // [lat, lng] of well
                return (
                  <React.Fragment key={`connection-${lease.leaseKey}-${idx}`}>
                    <Polyline
                      positions={connection.geometry.coordinates.map(coord => [coord[0], coord[1]])}
                      pathOptions={{
                        color: '#000',
                        weight: 1.5,
                        opacity: 0.6,
                        dashArray: '5, 5'
                      }}
                    >
                      <Popup>
                        <strong>Lease:</strong> {connection.properties.leaseKey}<br/>
                        <strong>Well:</strong> {connection.properties.wellName || 'Unknown'} ({connection.properties.apiNumber || 'N/A'})
                      </Popup>
                    </Polyline>
                    {/* Small dot at well end of connection line */}
                    <CircleMarker
                      center={[wellPosition[0], wellPosition[1]]}
                      radius={3}
                      pathOptions={{
                        fillColor: '#000',
                        color: '#000',
                        fillOpacity: 0.8,
                        weight: 1
                      }}
                    />
                  </React.Fragment>
                );
              });
            })}

            {/* Lease-well links from lease centroids to wells - visible when wells layer is selected, lease-well links are enabled, and clusters disappear (zoom 13+) */}
            {mapFeatureLayer === 'wells' && connectionLinesVisible && currentZoom >= 13 && leaseCentroids
              .filter((lease) => hasFlagsFromSelectedDatasets(lease.leaseKey, lease.county))
              .map((lease) => {
              const connections = createLeaseWellConnections(lease);
              return connections.map((connection, idx) => {
                const leasePosition = connection.geometry.coordinates[0]; // [lat, lng] of lease centroid
                return (
                  <React.Fragment key={`connection-wells-${lease.leaseKey}-${idx}`}>
                    <Polyline
                      positions={connection.geometry.coordinates.map(coord => [coord[0], coord[1]])}
                      pathOptions={{
                        color: '#000',
                        weight: 1.5,
                        opacity: 0.6,
                        dashArray: '5, 5'
                      }}
                    >
                      <Popup>
                        <strong>Lease:</strong> {connection.properties.leaseKey}<br/>
                        <strong>Well:</strong> {connection.properties.wellName || 'Unknown'} ({connection.properties.apiNumber || 'N/A'})
                      </Popup>
                    </Polyline>
                    {/* Small dot at lease centroid end of connection line (no dot at well end since we have well markers) */}
                    <CircleMarker
                      center={[leasePosition[0], leasePosition[1]]}
                      radius={3}
                      pathOptions={{
                        fillColor: '#000',
                        color: '#000',
                        fillOpacity: 0.8,
                        weight: 1
                      }}
                    />
                  </React.Fragment>
                );
              });
            })}

            {/* Well markers cluster - only when wells layer is selected */}
            {mapFeatureLayer === 'wells' && (
              <>
                {clustersEnabled ? (
                  <MarkerClusterGroup
                    disableClusteringAtZoom={13}
                    chunkedLoading={true}
                    maxClusterRadius={55}
                    spiderfyOnMaxZoom={false}
                    zoomToBoundsOnClick={true}
                  >
                    {wellMarkers}
                  </MarkerClusterGroup>
                ) : (
                  <>{wellMarkers}</>
                )}
                
                {/* Well name labels - outside cluster group so they don't get clustered */}
                {wellsLabelsVisible && currentZoom >= 12 && (() => {
                const wellsByLocation = new Map();
                
                wellsData
                  .filter(well => {
                    const lat = parseFloat(well['Latitude'] || well.latitude || well.lat);
                    const lng = parseFloat(well['Longitude'] || well.longitude || well.lng || well.lon);
                    return !isNaN(lat) && !isNaN(lng);
                  })
                  .forEach(well => {
                    const lat = parseFloat(well['Latitude'] || well.latitude || well.lat);
                    const lng = parseFloat(well['Longitude'] || well.longitude || well.lng || well.lon);
                    const apiNumber = well['API Number'] || well.api_number || '';
                    const locationKey = `${apiNumber}-${lat.toFixed(6)}-${lng.toFixed(6)}`;
                    
                    if (!wellsByLocation.has(locationKey)) {
                      wellsByLocation.set(locationKey, {
                        lat,
                        lng,
                        wells: []
                      });
                    }
                    
                    wellsByLocation.get(locationKey).wells.push(well);
                  });
                
                return Array.from(wellsByLocation.values()).map((locationGroup, idx) => {
                  const { lat, lng, wells } = locationGroup;
                  // Uppercase "Unknown Well" for display consistency
                  const wellNames = wells.map(w => {
                    const name = w['Well Name'] || w.well_name || 'Unknown Well';
                    return name === 'Unknown Well' ? 'UNKNOWN WELL' : name;
                  }).join(', ');
                  const maxRadius = Math.max(...wells.map(w => {
                    const style = getWellMarkerStyle(w, {
                      domainMin: wellSizeDomain.min,
                      domainMax: wellSizeDomain.max,
                    });
                    return style.radius;
                  }));
                  
                  // Determine label position: use bottomhole if horizBorehole is visible and bottomhole exists
                  let labelLat = lat;
                  let labelLng = lng;
                  
                  if (horizBoreholeVisible) {
                    // Check if any well in this group has valid bottomhole coordinates
                    for (const well of wells) {
                      const latB = parseFloat(well.latitude_b);
                      const lngB = parseFloat(well.longitude_b);
                      if (!isNaN(latB) && !isNaN(lngB) && (latB !== lat || lngB !== lng)) {
                        // Use bottomhole location for label
                        labelLat = latB;
                        labelLng = lngB;
                        break; // Use first valid bottomhole found
                      }
                    }
                  }
                  
                  const locationKey = `${locationGroup.wells[0]['API Number'] || locationGroup.wells[0].api_number || ''}-${lat.toFixed(6)}-${lng.toFixed(6)}`;
                  
                  return (
                    <Marker
                      key={`well-label-${locationKey}`}
                      position={[labelLat, labelLng]}
                      icon={L.divIcon({
                        className: 'well-label',
                        html: `<div style="
                          font-size: 10px;
                          font-weight: 600;
                          color: #333;
                          text-align: left;
                          white-space: nowrap;
                          pointer-events: none;
                          text-shadow:
                            0px 0px 4px rgba(255, 255, 255, 1),
                            0px 0px 6px rgba(255, 255, 255, 0.8);
                        ">${wellNames}</div>`,
                        iconSize: [200, 20],
                        iconAnchor: [-(maxRadius + 6), 0]
                      })}
                      interactive={false}
                    />
                  );
                });
              })()}
              </>
            )}

            {/* Lease markers cluster - only when leases layer is selected */}
            {mapFeatureLayer === 'leases' && (
              <>
                {clustersEnabled ? (
                  <MarkerClusterGroup
                    disableClusteringAtZoom={13}
                    chunkedLoading={true}
                    maxClusterRadius={55}
                    spiderfyOnMaxZoom={false}
                    zoomToBoundsOnClick={true}
                  >
                    {leaseMarkers}
                  </MarkerClusterGroup>
                ) : (
                  <>{leaseMarkers}</>
                )}
               
                {/* Lease name labels - outside cluster group so they don't get clustered */}
                {leasesLabelsVisible && currentZoom >= 12 && leaseCentroids
                  .filter(lease => {
                    // Filter out leases without flags from selected datasets if toggle is enabled
                    if (hideWithoutFlags) {
                      return hasFlagsFromSelectedDatasets(lease.leaseKey, lease.county);
                    }
                    return true;
                  })
                  .map((lease) => {
                  const labelFlagCount = getLeaseSelectedFlagCount(
                    redFlagsCache,
                    lease.leaseKey,
                    lease.county,
                    selectedFlagDatasets
                  );
                  const labelStyle = flagView
                    ? {
                        radius: Math.max(
                          4,
                          Math.round(
                            scaleLinear(
                              labelFlagCount,
                              leaseSizeDomain.min,
                              leaseSizeDomain.max,
                              LEASE_RADIUS_MIN,
                              LEASE_RADIUS_MAX
                            )
                          )
                        ),
                      }
                    : getLeaseMarkerStyle(
                        lease,
                        activeTabType,
                        leaseAggregatedData.get(lease.leaseKey) || {},
                        {
                          domainMin: leaseSizeDomain.min,
                          domainMax: leaseSizeDomain.max,
                        }
                      );
                  const markerSize = Math.max(labelStyle.radius * 2, 10);
                  return (
                    <Marker
                      key={`lease-label-${lease.leaseKey}`}
                      position={lease.centroid}
                      icon={L.divIcon({
                        className: 'lease-label',
                        html: `<div style="
                          font-size: 10px;
                          font-weight: 600;
                          color: #333;
                          text-align: left;
                          white-space: nowrap;
                          pointer-events: none;
                          text-shadow:
                            0px 0px 4px rgba(255, 255, 255, 1),
                            0px 0px 6px rgba(255, 255, 255, 0.8);
                        ">${lease.leaseName}</div>`,
                        iconSize: [200, 20],
                        iconAnchor: [-(markerSize / 2 + 6), 0]
                      })}
                      interactive={false}
                    />
                  );
                })}
              </>
            )}
          </>
        )}
      </MapContainer>
    </Box>
  );
};

export default EnhancedMap;

